const API_BASE_URL = '';

export interface DispatchQueue {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  strategy_type:
    'daily_deals' | 'high_ticket' | 'flash_sale' | 'mirror' | 'custom' | string;
  is_paused: boolean;
  priority: number;
  target_group_ids: string[];
  pacing_mode?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  prime_start_hour?: number;
  prime_end_hour?: number;
  prime_min_delay_minutes?: number;
  prime_max_delay_minutes?: number;
  day_min_delay_minutes?: number;
  day_max_delay_minutes?: number;
  created_at: string;
  updated_at: string;
  items_count?: number;
  pending_count?: number;
}

export interface CreateDispatchQueuePayload {
  name: string;
  slug?: string;
  strategy_type?: string;
  priority?: number;
  target_group_ids?: string[];
  pacing_mode?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  prime_start_hour?: number;
  prime_end_hour?: number;
  prime_min_delay_minutes?: number;
  prime_max_delay_minutes?: number;
  day_min_delay_minutes?: number;
  day_max_delay_minutes?: number;
}

export interface UpdateDispatchQueuePayload {
  name?: string;
  slug?: string;
  strategy_type?: string;
  priority?: number;
  target_group_ids?: string[];
  is_paused?: boolean;
  pacing_mode?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  prime_start_hour?: number;
  prime_end_hour?: number;
  prime_min_delay_minutes?: number;
  prime_max_delay_minutes?: number;
  day_min_delay_minutes?: number;
  day_max_delay_minutes?: number;
}

export interface QueueItem {
  id: string;
  tenant_id: string;
  queue_id?: string | null;
  queue_name?: string | null;
  offer_id: string;
  group_ids: string[];
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  scheduled_for: string;
  sent_at?: string | null;
  retry_count: number;
  max_retries: number;
  last_error?: string | null;
  delivered_group_ids?: string[];
  created_at: string;
  updated_at: string;
  offer_title?: string | null;
  offer_image_url?: string | null;
  offer_promotional_price?: number | null;
  offer_original_price?: number | null;
  offer_coupon_code?: string | null;
  offer_copy?: string | null;
  custom_title_override?: string | null;
  custom_copy_override?: string | null;
  custom_image_url_override?: string | null;
}

export interface UpdateQueueItemPayload {
  custom_title_override?: string | null;
  custom_copy_override?: string | null;
  custom_image_url_override?: string | null;
  send_as_next?: boolean;
}

export interface QueueCadenceTelemetry {
  queue_id?: string | null;
  dispatches_today: number;
  queued_count: number;
  failed_count: number;
  safe_capacity_daily: number;
  saturation_percentage: number;
  status_level: 'safe' | 'warning' | 'saturated';
  business_hours_active: boolean;
  business_hours_window: string;
  next_window_start?: string | null;
  jitter_range: string;
  is_paused: boolean;
  quiet_hours_active?: boolean;
  quiet_hours_window?: string;
  current_pacing_phase?: 'prime' | 'commercial' | 'quiet' | string;
}

export interface EnqueueOfferPayload {
  offer_id: string;
  group_ids: string[];
  queue_id?: string | null;
  scheduled_for?: string | null;
}

/**
 * Consulta os itens da esteira de disparos.
 */
export async function fetchQueueItems(
  token: string,
  statusFilter?: string,
  queueId?: string
): Promise<QueueItem[]> {
  const params = new URLSearchParams();
  if (statusFilter) {
    params.append('status', statusFilter);
  }
  if (queueId) {
    params.append('queue_id', queueId);
  }
  const url = `${API_BASE_URL}/api/queue${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao carregar itens da fila de disparo.');
  }

  return response.json();
}

/**
 * Adiciona uma oferta na esteira de disparos anti-ban com Jitter RNG.
 */
export async function enqueueOffer(
  token: string,
  payload: EnqueueOfferPayload
): Promise<QueueItem> {
  const response = await fetch(`${API_BASE_URL}/api/queue`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível agendar o disparo da oferta.'
    );
  }

  return response.json();
}

/**
 * Executa o Bypass imediato da fila (Envia Agora para os grupos).
 */
export async function dispatchQueueItemNow(
  token: string,
  queueId: string
): Promise<QueueItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/queue/${queueId}/dispatch-now`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Falha ao executar o envio imediato da oferta.'
    );
  }

  return response.json();
}

/**
 * Cancela um item agendado na esteira.
 */
export async function cancelQueueItem(
  token: string,
  queueId: string
): Promise<QueueItem> {
  const response = await fetch(`${API_BASE_URL}/api/queue/${queueId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível cancelar o item da fila.'
    );
  }

  return response.json();
}

/**
 * Retorna telemetria operacional de cadência, saturação e janela comercial.
 */
export async function fetchCadenceTelemetry(
  token: string,
  queueId?: string
): Promise<QueueCadenceTelemetry> {
  const params = new URLSearchParams();
  if (queueId) {
    params.append('queue_id', queueId);
  }
  const url = `${API_BASE_URL}/api/queue/cadence${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao carregar telemetria de cadência.');
  }

  return response.json();
}

export interface QueueSettings {
  dispatch_start_hour: number;
  dispatch_end_hour: number;
  min_delay_minutes: number;
  max_delay_minutes: number;
  safe_capacity_daily: number;
}

export async function fetchQueueSettings(
  token: string
): Promise<QueueSettings> {
  const response = await fetch(`${API_BASE_URL}/api/queue/settings`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao carregar configurações da fila.');
  }

  return response.json();
}

export async function updateQueueSettings(
  token: string,
  settings: QueueSettings
): Promise<QueueSettings> {
  const response = await fetch(`${API_BASE_URL}/api/queue/settings`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Falha ao salvar configurações da fila.'
    );
  }

  return response.json();
}

/**
 * Pausa a esteira global de disparos do tenant.
 */
export async function pauseQueue(
  token: string
): Promise<{ is_paused: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/queue/pause`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Não foi possível pausar a esteira.');
  }

  return response.json();
}

/**
 * Retoma a esteira global de disparos do tenant.
 */
export async function resumeQueue(
  token: string
): Promise<{ is_paused: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/queue/resume`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Não foi possível retomar a esteira.');
  }

  return response.json();
}

/**
 * Executa ações em lote sobre múltiplos itens da esteira ('cancel', 'retry' ou 'requeue').
 */
export async function batchQueueAction(
  token: string,
  action: 'cancel' | 'retry' | 'requeue',
  queueIds: string[]
): Promise<{ success: boolean; affected_count: number; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/queue/batch-action`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, queue_ids: queueIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao executar ação em lote.');
  }

  return response.json();
}

/**
 * Reordena os itens pendentes da esteira.
 */
export async function reorderQueueItems(
  token: string,
  orderedIds: string[]
): Promise<{ success: boolean; affected_count: number; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/queue/reorder`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao reordenar itens da fila.');
  }

  return response.json();
}

/**
 * Consulta todas as filas de despacho configuradas no tenant.
 */
export async function fetchDispatchQueues(
  token: string
): Promise<DispatchQueue[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queues`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao carregar as filas de disparo.');
  }

  return response.json();
}

/**
 * Cria uma nova fila de disparo segmentada.
 */
export async function createDispatchQueue(
  token: string,
  payload: CreateDispatchQueuePayload
): Promise<DispatchQueue> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queues`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível criar a fila de disparo.'
    );
  }

  return response.json();
}

/**
 * Atualiza propriedades e configurações de uma fila de disparo existente.
 */
export async function updateDispatchQueue(
  token: string,
  queueId: string,
  payload: UpdateDispatchQueuePayload
): Promise<DispatchQueue> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queues/${queueId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível atualizar a fila de disparo.'
    );
  }

  return response.json();
}

/**
 * Exclui uma fila de disparo e remove o vínculo dos itens pendentes.
 */
export async function deleteDispatchQueue(
  token: string,
  queueId: string
): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queues/${queueId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível excluir a fila de disparo.'
    );
  }

  return response.json();
}

/**
 * Pausa independentemente uma fila específica de disparo.
 */
export async function pauseDispatchQueue(
  token: string,
  queueId: string
): Promise<{ is_paused: boolean; queue_id: string; message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/queues/${queueId}/pause`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Não foi possível pausar a fila.');
  }

  return response.json();
}

/**
 * Retoma independentemente uma fila específica de disparo.
 */
export async function resumeDispatchQueue(
  token: string,
  queueId: string
): Promise<{ is_paused: boolean; queue_id: string; message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/queues/${queueId}/resume`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Não foi possível retomar a fila.');
  }

  return response.json();
}

/**
 * Atualiza overrides manuais de cópia, imagem e título de um item pendente da esteira,
 * com suporte à opção 'send_as_next' (furar a fila).
 */
export async function updateQueueItem(
  token: string,
  itemId: string,
  payload: UpdateQueueItemPayload
): Promise<QueueItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queue/${itemId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao atualizar item da fila.');
  }

  return response.json();
}

export interface BulkAddQueuePayload {
  offer_ids: string[];
  queue_id?: string | null;
  group_ids?: string[];
}

export interface BulkAddQueueResponse {
  success_count: number;
  failed_count: number;
  enqueued_items: QueueItem[];
  errors: string[];
}

/**
 * Envia múltiplas ofertas em lote para uma fila de destino selecionada.
 */
export async function bulkAddQueue(
  token: string,
  payload: BulkAddQueuePayload
): Promise<BulkAddQueueResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/queue/bulk-add`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Não foi possível adicionar ofertas à fila em lote.'
    );
  }

  return response.json();
}
